// Shared with mju-aioe/packages/shared/src/schemas/projectWork.ts — keep the two identical.
/**
 * A project's own work layer, above its repositories
 * (docs/plans/2026-10-project-level-work.md): a project roadmap, a project
 * board whose tasks delegate to repository tasks, and a project architecture.
 * The same records in every edition; what differs is only where they live —
 * the organisation (AIOE) for a project registered with it (every one is the
 * organisation's from AIOE 0.9.0), the team's hub for one shared with the
 * team, the desktop's private hub for one kept on this computer (the Personal
 * space). All three serve these routes with these shapes:
 *
 *   GET /projects/v1/:id/roadmap          { revision, updatedAt?, updatedBy?, schemaVersion, vision?, rows[] } + ETag "<revision>"
 *   PUT /projects/v1/:id/roadmap          the whole document; If-Match "<revision>" → 412 precondition_failed { current }
 *   GET /projects/v1/:id/architecture     { revision, updatedAt?, updatedBy?, schemaVersion, title?, summary?, template?, layers[], areas{}, connections[] }
 *   PUT /projects/v1/:id/architecture     the whole document; If-Match as above
 *   …/projects/v1/:id/boards/project      the project board: the per-repository board routes, mounted without a root
 *                                         (GET, POST /tasks, PATCH /tasks/:taskId, POST /tasks/:taskId/comments,
 *                                         DELETE /tasks/:taskId); everything that carries a rootId says "" for it
 *   GET /projects/v1/:id/map              the composite gains `project: { architecture?, roadmap? }` (./projectMap.ts)
 *   GET /projects/v1/:id/activity?since=&limit=   who did what on the project, newest last: { seq, entries[], people{} }
 *
 * **People.** Every write is one person's: the organisation's principal, the
 * team hub's person, or — on a desktop's private hub — its one owner. An
 * agent acting for them is named beside them (`actor`), never instead of
 * them. The server stamps what changed (`updatedBy`, `updatedAt` on the
 * document, and on each roadmap row that differs from the one before): a
 * client cannot sign for someone else. Two people editing different rows do
 * not collide — a 412 hands back the current document, and the change is
 * applied to it again (`applyRoadmapEdit`); the same row is last writer
 * wins, with both writes in the activity.
 *
 * **Activity.** Each home keeps one log per project of what changed in its
 * work layer and on the boards it keeps, written by the server from the
 * writes it accepts ({@link roadmapActivity}, {@link taskActivity},
 * {@link architectureActivity}) — never posted by a client. A repository's
 * own audit stays the record of what happened inside it.
 *
 * `revision` is the server's integer, starting at 1 with the first PUT (a GET
 * before it answers revision 0 and an empty document). A PUT with no If-Match
 * (or `*`) is unconditional. `schemaVersion` is the document's own shape.
 * Contributors and above write; viewers read; anyone else gets 404.
 *
 * **Delegation.** A project roadmap row or project task is not worked
 * directly: it names the repository rows or tasks that carry it, and its
 * state is rolled up from them. The project's own `status` is never
 * overwritten by a roll-up — a person may close a project task by hand, and
 * that is simply `status: "done"`. The roll-up functions below are the one
 * definition every server and the desktop use.
 *
 *   roadmap row   `delegates: [{ rootId, id }]` → `rolledUp: { status, from, missing }` on GET (output only)
 *   project task  `metadata.delegates: [{ rootId, board, uid }]` → `metadata.rollup: { status, counts, missing, at }`
 *   repository task `metadata.parent: { projectId, board: "project", uid }`
 *
 * Links are by `uid`, never the board-local `T-n` (a shared board renumbers).
 * Creating a repository task with `metadata.parent` adds it to the parent's
 * `delegates`; deleting it removes it; both recompute the roll-up.
 *
 * Words for people: project roadmap, project task, delegated to, rolled up
 * from, project architecture, connection.
 *
 * AIOE 0.35.0 (D247, B-12): a project roadmap row may carry `owner: { id?, name }`, a person on the project; the console picks one from the
 * members and shows the name quietly. A needs-you record about the row (a blocked task serving it) goes to the owner, else the project's owners.
 */
import { z } from "zod";
import { partStatusOf, PROJECT_BOARD_ID, PROJECT_LEVEL_ROOT_ID, TASK_CLOSURE_KINDS, taskClosureKindSchema, TASK_ROLLUP_STATUSES, taskDelegateSchema, taskParentSchema, taskRollupSchema } from "./projectLinks.js";
import { roadmapStatusSchema, ownerRefSchema } from "./projectMap.js";
export const PROJECT_WORK_SCHEMA_VERSION = 1;
export { partStatusOf, PROJECT_BOARD_ID, PROJECT_LEVEL_ROOT_ID, TASK_CLOSURE_KINDS, taskClosureKindSchema, TASK_ROLLUP_STATUSES, taskDelegateSchema, taskParentSchema, taskRollupSchema };
const id = z.string().min(1).max(200);
const by = z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() });
// ── The project roadmap ────────────────────────────────────────────────
/** A row of one repository's roadmap (`docs/roadmap/README.md`), by the repository and the row's id there. */
export const roadmapDelegateSchema = z.object({ rootId: id, id });
/** When a row with delegates counts as done: every one of them, or any. */
export const roadmapRollupRuleSchema = z.enum(["all", "any"]);
/** A roadmap row's target: a month, a day of a month, or a quarter. */
export const ROADMAP_TARGET_PATTERN = /^\d{4}-(0[1-9]|1[0-2])(-\d{2})?$|^\d{4}-Q[1-4]$/;
export const projectRoadmapRowSchema = z.looseObject({
    /** `R-n`, the project's own numbering, apart from every repository's. */
    id,
    title: z.string().max(500),
    /** A project architecture layer's id, or free text. */
    area: z.string().max(500).optional(),
    phase: z.string().max(50).default(""),
    /** The row's own status: what it is when it has no delegates, and what a person set. */
    status: roadmapStatusSchema,
    evidence: z.string().max(4000).default(""),
    dependsOn: z.array(id).max(100).optional(),
    delegates: z.array(roadmapDelegateSchema).max(200).default([]),
    rollup: roadmapRollupRuleSchema.optional(),
    /**
     * When the row is aimed at, as a repository roadmap's Target column: a month `YYYY-MM`, a day
     * `YYYY-MM-DD` or a quarter `YYYY-Q1`…`Q4`. An empty string clears it (the row is stored without).
     */
    target: z.union([z.literal(""), z.string().max(10).regex(ROADMAP_TARGET_PATTERN, "a target is YYYY-MM, YYYY-MM-DD or YYYY-Q1 to Q4")]).optional(),
    /** Who is accountable for the row (AIOE 0.35.0, D247, B-12): a person on the project; `id` when the home knows them, always `name`. */
    owner: ownerRefSchema.optional(),
    updatedAt: z.string().max(50).optional(),
    updatedBy: by.optional(),
});
export const projectRoadmapPutSchema = z.object({
    schemaVersion: z.literal(PROJECT_WORK_SCHEMA_VERSION),
    /** What the project is for, above the rows (as a repository roadmap's opening paragraphs). */
    vision: z.string().max(8000).optional(),
    rows: z.array(projectRoadmapRowSchema).max(2000),
});
/**
 * What a project row's delegates add up to, or undefined for a row with none.
 * `find` answers a delegated row's status in its repository's roadmap, or
 * undefined when it is not known. A row the repository will not do is left
 * out of the count: it neither holds the project row open nor closes it.
 *
 *   done          rule "any": one delegate is done; rule "all" (the default): every counted delegate is done and none is missing
 *   blocked       otherwise, one is deferred
 *   in-progress   otherwise, one is in progress or done
 *   not-started   otherwise
 */
export function rollUpRoadmapRow(row, find) {
    const delegates = row.delegates ?? [];
    if (!delegates.length)
        return undefined;
    let from = 0;
    let missing = 0;
    const counted = [];
    for (const d of delegates) {
        const status = find(d);
        if (!status) {
            missing++;
            continue;
        }
        from++;
        if (status !== "wont-do")
            counted.push(status);
    }
    const done = counted.filter((s) => s === "done").length;
    const allDone = counted.length > 0 && done === counted.length && missing === 0;
    let status;
    if (row.rollup === "any" ? done > 0 : allDone)
        status = "done";
    else if (counted.includes("deferred"))
        status = "blocked";
    else if (done > 0 || counted.includes("in-progress"))
        status = "in-progress";
    else
        status = "not-started";
    return { status, from, missing };
}
/** A project row's state as shown: its own once a person settled it, else what its delegates add up to, else its own. */
export function effectiveRoadmapStatus(row) {
    if (row.status === "done" || row.status === "wont-do" || row.status === "deferred")
        return row.status;
    return row.rolledUp ? row.rolledUp.status : row.status;
}
/** The next free `R-n` in a roadmap. */
export function nextProjectRoadmapId(rows) {
    let max = 0;
    for (const r of rows) {
        const m = /^R-(\d+)$/.exec(r.id);
        if (m)
            max = Math.max(max, Number(m[1]));
    }
    return `R-${max + 1}`;
}
// ── The project board: tasks that delegate ─────────────────────────────
/**
 * What a project task's delegates add up to. One entry per delegate: the
 * repository task's status, or undefined when the task is not there.
 *
 *   blocked       one is blocked, or was cancelled (reason `part_cancelled`; pass "cancelled" for a task closed as cancelled)
 *   in-progress   otherwise, one is in progress — or some are done or in review while others have not begun
 *   done          otherwise, every one is done and none is missing
 *   review        otherwise, every one is in review or done and none is missing
 *   not-started   otherwise
 */
export function rollUpTask(statuses, at) {
    const counts = { done: 0, review: 0, "in-progress": 0, blocked: 0, other: 0, cancelled: 0 };
    let missing = 0;
    for (const s of statuses) {
        if (s === undefined)
            missing++;
        else if (s === "done" || s === "review" || s === "in-progress" || s === "blocked" || s === "cancelled")
            counts[s]++;
        else
            counts.other++;
    }
    const found = statuses.length - missing;
    let status;
    // A cancelled part (a task closed as cancelled: the caller passes "cancelled") holds the project task at blocked; it is never done.
    if (counts.cancelled > 0)
        status = "blocked";
    else if (counts.blocked > 0)
        status = "blocked";
    else if (counts["in-progress"] > 0)
        status = "in-progress";
    else if (found > 0 && missing === 0 && counts.done === found)
        status = "done";
    else if (found > 0 && missing === 0 && counts.done + counts.review === found)
        status = "review";
    else if (counts.done + counts.review > 0)
        status = "in-progress";
    else
        status = "not-started";
    return { status, counts, missing, at, ...(counts.cancelled > 0 ? { reason: "part_cancelled" } : {}) };
}
/** Whether two roll-ups say the same thing (so an unchanged one is not written again). */
export function sameRollup(a, b) {
    if (!a || !b)
        return a === b;
    return a.status === b.status && a.missing === b.missing && a.counts.done === b.counts.done && a.counts.review === b.counts.review && a.counts["in-progress"] === b.counts["in-progress"] && a.counts.blocked === b.counts.blocked && a.counts.other === b.counts.other && (a.counts.cancelled ?? 0) === (b.counts.cancelled ?? 0) && a.reason === b.reason;
}
/** A task's `metadata.delegates`, when it has any well-formed ones. */
export function taskDelegates(task) {
    const raw = task?.metadata?.delegates;
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const d of raw) {
        const parsed = taskDelegateSchema.safeParse(d);
        if (parsed.success && !out.some((x) => x.rootId === parsed.data.rootId && x.board === parsed.data.board && x.uid === parsed.data.uid))
            out.push(parsed.data);
    }
    return out;
}
/** A repository task's `metadata.parent`, when it is well-formed. */
export function taskParent(task) {
    const parsed = taskParentSchema.safeParse(task?.metadata?.parent);
    return parsed.success ? parsed.data : undefined;
}
/** A project task's `metadata.rollup`, when it is well-formed. */
export function taskRollup(task) {
    const parsed = taskRollupSchema.safeParse(task?.metadata?.rollup);
    return parsed.success ? parsed.data : undefined;
}
/**
 * A project task's state as shown: done once a person closed it; otherwise
 * what its delegates add up to once any has begun; otherwise its own.
 */
export function effectiveTaskStatus(task) {
    if (task.status === "done")
        return "done";
    if (!taskDelegates(task).length)
        return task.status;
    const rollup = taskRollup(task);
    return rollup && rollup.status !== "not-started" ? rollup.status : task.status;
}
// ── The project architecture ───────────────────────────────────────────
/** `<rootId>:<path>` — a repository's area (a folder of it), or `<rootId>:*` for the whole repository in a layer's `match`. */
const areaRef = z.string().min(1).max(700);
export const projectLayerSchema = z.looseObject({
    id,
    title: z.string().max(200),
    description: z.string().max(2000).optional(),
    /** `<rootId>:<path>` names that area; `<rootId>:<path>/*` it and everything under it; `<rootId>:*` the whole repository. The most specific wins. */
    match: z.array(areaRef).max(500).default([]),
});
export const projectAreaNoteSchema = z.looseObject({ title: z.string().max(200).optional(), description: z.string().max(2000).optional(), layer: id.optional() });
export const projectConnectionKindSchema = z.enum(["http", "package", "protocol", "data", "other"]);
/** Something one repository (or an area of it) relies on in another: the one edge a repository's own map cannot hold. */
export const projectConnectionSchema = z.looseObject({
    /** `<rootId>` or `<rootId>:<path>`. */
    from: areaRef,
    to: areaRef,
    kind: projectConnectionKindSchema,
    label: z.string().max(200).optional(),
    description: z.string().max(2000).optional(),
});
/**
 * The architecture template a picture follows (roadmap D111): its id where it was published
 * (`catalogue:<entry>` for the organisation's or the team hub's, `repo:<file>` for a repository's),
 * its name, and where it came from. Recorded so the picture can say which template it follows and
 * where it departs from it; `source` is a plain string so a source added later never refuses a write.
 */
export const architectureTemplateRefSchema = z.looseObject({
    id: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    source: z.string().max(40).optional(),
});
export const projectArchitecturePutSchema = z.object({
    schemaVersion: z.literal(PROJECT_WORK_SCHEMA_VERSION),
    title: z.string().max(200).optional(),
    summary: z.string().max(4000).optional(),
    /** The architecture template the picture follows (D111). Optional: a picture drawn freehand has none. */
    template: architectureTemplateRefSchema.optional(),
    layers: z.array(projectLayerSchema).max(50).default([]),
    areas: z
        .record(areaRef, projectAreaNoteSchema)
        .default({})
        .refine((a) => Object.keys(a).length <= 2000, "at most 2000 areas"),
    connections: z.array(projectConnectionSchema).max(1000).default([]),
});
/** `<rootId>:<path>` for a repository's area. */
export function areaRefOf(rootId, path) {
    return `${rootId}:${path}`;
}
/**
 * The repository and path an area reference names. With the project's
 * repository ids given, the longest one the reference starts with wins (an
 * id may itself hold a colon); without them, the first colon divides.
 * A bare `<rootId>` is the whole repository (`path: ""`).
 */
export function parseAreaRef(ref, rootIds) {
    if (rootIds) {
        let best;
        for (const r of rootIds)
            if ((ref === r || ref.startsWith(`${r}:`)) && (!best || r.length > best.length))
                best = r;
        if (best !== undefined)
            return { rootId: best, path: ref.length > best.length ? ref.slice(best.length + 1) : "" };
    }
    const at = ref.indexOf(":");
    return at < 0 ? { rootId: ref, path: "" } : { rootId: ref.slice(0, at), path: ref.slice(at + 1) };
}
/**
 * How well a layer's `match` entry covers an area of a repository: 0 when it
 * does not, else higher for the more specific (exact area, then the longest
 * `…/*` prefix, then `<rootId>:*`).
 */
export function layerMatchScore(entry, rootId, path, rootIds) {
    const m = parseAreaRef(entry, rootIds);
    if (m.rootId !== rootId)
        return 0;
    if (m.path === "*" || m.path === "")
        return 1;
    if (m.path === path)
        return 100_000;
    if (m.path.endsWith("/*")) {
        const base = m.path.slice(0, -2);
        if (path === base || path.startsWith(`${base}/`))
            return 2 + base.length;
    }
    return 0;
}
/** The project layer an area of a repository belongs to, or undefined when none claims it. */
export function projectLayerOf(doc, rootId, path, rootIds) {
    const noted = doc.areas?.[areaRefOf(rootId, path)]?.layer;
    if (noted && doc.layers.some((l) => l.id === noted))
        return noted;
    let best;
    for (const layer of doc.layers) {
        for (const entry of layer.match ?? []) {
            const score = layerMatchScore(entry, rootId, path, rootIds);
            if (score > (best?.score ?? 0))
                best = { id: layer.id, score };
        }
    }
    return best?.id;
}
/** An empty document of each kind: what a GET answers before the first PUT. */
export const EMPTY_PROJECT_ROADMAP = { revision: 0, schemaVersion: PROJECT_WORK_SCHEMA_VERSION, rows: [] };
export const EMPTY_PROJECT_ARCHITECTURE = { revision: 0, schemaVersion: PROJECT_WORK_SCHEMA_VERSION, layers: [], areas: {}, connections: [] };
/** `If-Match` as a revision: undefined for none or `*` (an unconditional write), NaN for one that is not a revision. */
export function ifMatchRevision(header) {
    const raw = header?.trim();
    if (!raw || raw === "*")
        return undefined;
    const m = /^(?:W\/)?"?(\d+)"?$/.exec(raw);
    return m ? Number(m[1]) : Number.NaN;
}
export const ROADMAP_ROLLED_UP_TEXT = { "not-started": "Not started", "in-progress": "In progress", done: "Done", blocked: "Blocked" };
export const TASK_ROLLUP_TEXT = { blocked: "Blocked", "in-progress": "In progress", review: "In review", done: "Done", "not-started": "Not started" };
export const PROJECT_ACTIVITY_KINDS = [
    "roadmap.row.added",
    "roadmap.row.changed",
    "roadmap.row.removed",
    "roadmap.row.delegated",
    "roadmap.vision",
    "architecture.changed",
    "task.created",
    "task.changed",
    "task.status",
    "task.comment",
    "task.removed",
    "task.delegated",
    "task.undelegated",
    "task.rolled-up",
    // The project's risks and issues (./projectRisks.ts `riskActivity`).
    "risk.raised",
    "risk.changed",
    "risk.status",
    "risk.removed",
    // The project's pictures (./projectPictures.ts `pictureActivity`).
    "picture.saved",
    "picture.removed",
];
const quoted = (title) => `“${title.length > 80 ? `${title.slice(0, 79)}…` : title}”`;
const STATUS_WORDS = { "not-started": "not started", "in-progress": "in progress", "wont-do": "won't do", todo: "to do", review: "in review" };
const statusWord = (s) => STATUS_WORDS[s] ?? s;
/**
 * A value as text that is the same however its objects' keys are ordered
 * (and with `undefined` members left out, as JSON leaves them): what "did it
 * change" compares, so a store or a client that re-serialises a document
 * with its keys in another order changes nothing.
 */
export function canonical(value) {
    if (Array.isArray(value))
        return `[${value.map((v) => canonical(v === undefined ? null : v)).join(",")}]`;
    if (value && typeof value === "object") {
        const o = value;
        return `{${Object.keys(o)
            .filter((k) => o[k] !== undefined)
            .sort()
            .map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value ?? null);
}
/** A row's content, for telling whether it changed: everything but its stamps and what the server computes. */
function rowContent(row) {
    const { updatedAt: _a, updatedBy: _b, rolledUp: _r, ...rest } = row;
    return canonical(rest);
}
/**
 * The rows as they are stored: each one that is new or differs from the one
 * before is stamped with who and when; an unchanged row keeps its stamp,
 * whatever the client sent. With what happened, for the activity.
 */
export function stampRoadmapRows(previous, next, by, at) {
    const before = new Map((previous ?? []).map((r) => [r.id, r]));
    const activity = [];
    const stamp = { updatedAt: at, ...(by.principal || by.actor ? { updatedBy: { ...(by.principal ? { principal: by.principal } : {}), ...(by.actor ? { actor: by.actor } : {}) } } : {}) };
    const rows = next.map((row) => {
        const { updatedAt: _a, updatedBy: _b, ...given } = row;
        // An empty target clears it.
        const { target: givenTarget, ...rest } = given;
        const content = givenTarget ? given : rest;
        row = content;
        const was = before.get(row.id);
        if (!was) {
            activity.push({ kind: "roadmap.row.added", rootId: PROJECT_LEVEL_ROOT_ID, target: row.id, text: `added ${row.id} ${quoted(row.title)} to the project roadmap` });
            for (const d of row.delegates ?? [])
                activity.push({ kind: "roadmap.row.delegated", rootId: PROJECT_LEVEL_ROOT_ID, target: row.id, text: `delegated ${row.id} to ${d.rootId} (${d.id})`, detail: { rootId: d.rootId, id: d.id } });
            return { ...content, ...stamp };
        }
        if (rowContent(was) === rowContent(row)) {
            return { ...content, ...(was.updatedAt ? { updatedAt: was.updatedAt } : {}), ...(was.updatedBy ? { updatedBy: was.updatedBy } : {}) };
        }
        const changed = [];
        if (was.status !== row.status)
            changed.push(`status ${statusWord(was.status)} → ${statusWord(row.status)}`);
        if (was.title !== row.title)
            changed.push("title");
        if ((was.phase ?? "") !== (row.phase ?? ""))
            changed.push("phase");
        if ((was.area ?? "") !== (row.area ?? ""))
            changed.push("area");
        if ((was.evidence ?? "") !== (row.evidence ?? ""))
            changed.push("evidence");
        if (canonical(was.dependsOn ?? []) !== canonical(row.dependsOn ?? []))
            changed.push("what it waits on");
        if ((was.rollup ?? "all") !== (row.rollup ?? "all"))
            changed.push("how it rolls up");
        if ((was.target ?? "") !== (row.target ?? ""))
            changed.push(row.target ? `target ${row.target}` : "no target");
        const had = new Set((was.delegates ?? []).map((d) => `${d.rootId}\u0000${d.id}`));
        const has = new Set((row.delegates ?? []).map((d) => `${d.rootId}\u0000${d.id}`));
        for (const d of row.delegates ?? [])
            if (!had.has(`${d.rootId}\u0000${d.id}`))
                activity.push({ kind: "roadmap.row.delegated", rootId: PROJECT_LEVEL_ROOT_ID, target: row.id, text: `delegated ${row.id} to ${d.rootId} (${d.id})`, detail: { rootId: d.rootId, id: d.id } });
        const dropped = (was.delegates ?? []).filter((d) => !has.has(`${d.rootId}\u0000${d.id}`));
        if (dropped.length)
            changed.push(`no longer delegated to ${dropped.map((d) => `${d.rootId} (${d.id})`).join(", ")}`);
        if (changed.length)
            activity.push({ kind: "roadmap.row.changed", rootId: PROJECT_LEVEL_ROOT_ID, target: row.id, text: `changed ${row.id}: ${changed.join(", ")}`, ...(was.status !== row.status ? { detail: { from: was.status, to: row.status } } : {}) });
        return { ...content, ...stamp };
    });
    const kept = new Set(next.map((r) => r.id));
    for (const was of previous ?? [])
        if (!kept.has(was.id))
            activity.push({ kind: "roadmap.row.removed", rootId: PROJECT_LEVEL_ROOT_ID, target: was.id, text: `removed ${was.id} ${quoted(was.title)} from the project roadmap` });
    return { rows, activity };
}
/** What a roadmap write did, beyond its rows. */
export function roadmapActivity(previous, next) {
    return (previous?.vision ?? "") !== (next.vision ?? "") ? [{ kind: "roadmap.vision", rootId: PROJECT_LEVEL_ROOT_ID, text: "rewrote what the project is for" }] : [];
}
/** What an architecture write did. */
export function architectureActivity(previous, next) {
    const parts = [];
    const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
    if (!previous)
        parts.push(`${count(next.layers.length, "layer", "layers")} and ${count(next.connections.length, "connection", "connections")}`);
    else {
        if (canonical(previous.layers) !== canonical(next.layers))
            parts.push(`layers (${next.layers.length})`);
        if (canonical(previous.connections) !== canonical(next.connections))
            parts.push(`connections (${next.connections.length})`);
        if (canonical(previous.areas) !== canonical(next.areas))
            parts.push("what areas are called");
        if ((previous.title ?? "") !== (next.title ?? "") || (previous.summary ?? "") !== (next.summary ?? ""))
            parts.push("its description");
    }
    if (!parts.length)
        return [];
    return [{ kind: "architecture.changed", rootId: PROJECT_LEVEL_ROOT_ID, text: previous ? `changed the project architecture: ${parts.join(", ")}` : `drew the project architecture: ${parts.join(", ")}` }];
}
/** Where a delegated task sits, in words: the repository, and its board unless it is the repository's own tasks. */
function delegateWords(d) {
    return d.board === "solo" ? d.rootId : `${d.rootId} (${d.board})`;
}
/**
 * What a board write did, on the project board (`rootId` "") or a
 * repository's. `before` undefined is a task made; `after` undefined, one
 * removed. A write that only moved the roll-up is told by `rolledUp`.
 */
export function taskActivity(before, after, rootId, board) {
    const where = { rootId, detail: { board } };
    if (!before && !after)
        return [];
    if (!before && after) {
        const out = [{ kind: "task.created", ...where, target: after.id, text: `created ${after.id} ${quoted(after.title)}` }];
        const parent = taskParent(after);
        if (parent)
            out[0].text += ", carrying part of a project task";
        for (const d of taskDelegates(after))
            out.push({ kind: "task.delegated", rootId, target: after.id, text: `delegated ${after.id} to ${delegateWords(d)}`, detail: { board, to: d } });
        return out;
    }
    if (before && !after)
        return [{ kind: "task.removed", ...where, target: before.id, text: `removed ${before.id} ${quoted(before.title)}` }];
    const was = before;
    const now = after;
    const out = [];
    if (was.status !== now.status)
        out.push({ kind: "task.status", rootId, target: now.id, text: `moved ${now.id} to ${statusWord(now.status)}`, detail: { board, from: was.status, to: now.status } });
    const had = taskDelegates(was);
    const has = taskDelegates(now);
    const key = (d) => `${d.rootId}\u0000${d.board}\u0000${d.uid}`;
    for (const d of has)
        if (!had.some((x) => key(x) === key(d)))
            out.push({ kind: "task.delegated", rootId, target: now.id, text: `delegated ${now.id} to ${delegateWords(d)}`, detail: { board, to: d } });
    for (const d of had)
        if (!has.some((x) => key(x) === key(d)))
            out.push({ kind: "task.undelegated", rootId, target: now.id, text: `${now.id} is no longer delegated to ${delegateWords(d)}`, detail: { board, from: d } });
    const rolledWas = taskRollup(was);
    const rolledNow = taskRollup(now);
    // Delegates that have not begun are not news: only a roll-up that moved is.
    if (rolledNow && rolledNow.status !== (rolledWas?.status ?? "not-started")) {
        const total = has.length;
        out.push({ kind: "task.rolled-up", rootId, target: now.id, text: `${now.id} rolled up to ${statusWord(rolledNow.status)} (${rolledNow.counts.done} of ${total} done)`, detail: { board, status: rolledNow.status } });
    }
    if ((now.comments?.length ?? 0) > (was.comments?.length ?? 0))
        out.push({ kind: "task.comment", ...where, target: now.id, text: `commented on ${now.id}` });
    const changed = [];
    if (was.title !== now.title)
        changed.push("title");
    if ((was.assignee ?? "") !== (now.assignee ?? ""))
        changed.push(now.assignee ? `assigned to ${now.assignee}` : "unassigned");
    if (changed.length)
        out.push({ kind: "task.changed", ...where, target: now.id, text: `changed ${now.id}: ${changed.join(", ")}` });
    return out;
}
/** Add a row, or replace the one of its id. */
export function upsertRoadmapRow(row) {
    return (current) => {
        const at = current.rows.findIndex((r) => r.id === row.id);
        const rows = at >= 0 ? current.rows.map((r, i) => (i === at ? row : r)) : [...current.rows, row];
        return { ...current, rows };
    };
}
/** Drop a row, and every other row's wait on it. */
export function removeRoadmapRow(id) {
    return (current) => ({ ...current, rows: current.rows.filter((r) => r.id !== id).map((r) => (r.dependsOn?.includes(id) ? { ...r, dependsOn: r.dependsOn.filter((d) => d !== id) } : r)) });
}
/** A roadmap as a PUT takes it, from what a GET answered: without the server's revision, stamps and roll-ups. */
export function roadmapDocument(view) {
    return {
        schemaVersion: PROJECT_WORK_SCHEMA_VERSION,
        ...(view?.vision ? { vision: view.vision } : {}),
        rows: (view?.rows ?? []).map(({ rolledUp: _r, ...row }) => row),
    };
}
/** An architecture as a PUT takes it, from what a GET answered. */
export function architectureDocument(view) {
    const { revision: _r, updatedAt: _a, updatedBy: _b, ...doc } = view ?? EMPTY_PROJECT_ARCHITECTURE;
    return { ...doc, schemaVersion: PROJECT_WORK_SCHEMA_VERSION };
}
//# sourceMappingURL=projectWork.js.map